// Single source of truth for admin auth state used across all admin pages.
// Stores JWT in sessionStorage (tab-scoped, cleared on tab close — limits the
// XSS exfiltration window compared with localStorage).
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'grt_admin_token';

export const tokenStore = {
  get: () => sessionStorage.getItem(TOKEN_KEY),
  set: (t) => sessionStorage.setItem(TOKEN_KEY, t),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
};

export function authHeaders(token) {
  return { Authorization: `Bearer ${token || tokenStore.get()}` };
}

export function useAdminAuth() {
  const [token, setToken] = useState(() => tokenStore.get());
  const [me, setMe] = useState(null); // { username, role, totp_enabled } | null
  const [checking, setChecking] = useState(Boolean(token));

  const logout = useCallback(() => {
    tokenStore.clear();
    setToken(null);
    setMe(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders(token) });
        if (!cancelled) setMe(data);
      } catch {
        if (!cancelled) logout();
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, logout]);

  const loginSuccess = useCallback((newToken) => {
    tokenStore.set(newToken);
    setToken(newToken);
    setChecking(true);
  }, []);

  return { token, me, checking, loginSuccess, logout, setMe };
}
